import { Loader2 } from "lucide-react"
import { usePermitRequestActionsContext } from "modules/permit/context/PermitRequestActionsContext"
import { Button } from "shared/ui/button"

const THAI_SUBMIT_LABEL = "\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34"
const THAI_EXTRA_ACTION_LABEL = "\u0e08\u0e31\u0e14\u0e01\u0e32\u0e23\u0e40\u0e1e\u0e34\u0e48\u0e21\u0e40\u0e15\u0e34\u0e21"

export interface BottomActionBarExtraAction {
  label: string
  onClick: () => void
  disabled?: boolean
  className?: string
}

interface BottomActionBarProps {
  disabled?: boolean
  saveDisabled?: boolean
  showSaveButton?: boolean
  message?: string | null
  error?: string | null
  onReference?: () => void
  referenceDisabled?: boolean
  referenceLabel?: string
  onSimulate?: () => void
  isSimulating?: boolean
  onSubmit?: () => void
  submitDisabled?: boolean
  submitLabel?: string
  showSubmitButton?: boolean
  onApprove?: () => void
  approveDisabled?: boolean
  approveLabel?: string
  onReject?: () => void
  rejectDisabled?: boolean
  rejectLabel?: string
  onDisapprove?: () => void
  disapproveDisabled?: boolean
  disapproveLabel?: string
  onCancel?: () => void
  cancelDisabled?: boolean
  cancelLabel?: string
  onTimeline?: () => void
  timelineDisabled?: boolean
  isTimelineLoading?: boolean
  timelineLabel?: string
  onExtraAction?: () => void
  extraActionDisabled?: boolean
  extraActionLabel?: string
  extraActionClassName?: string
  extraActions?: BottomActionBarExtraAction[]
}

export function BottomActionBar({
  disabled = false,
  saveDisabled = false,
  showSaveButton = true,
  message,
  error,
  onReference,
  referenceDisabled = false,
  referenceLabel = "\u0e43\u0e0a\u0e49 Permit \u0e2d\u0e49\u0e32\u0e07\u0e2d\u0e34\u0e07",
  onSimulate,
  isSimulating = false,
  onSubmit,
  submitDisabled = false,
  submitLabel = THAI_SUBMIT_LABEL,
  showSubmitButton = true,
  onApprove,
  approveDisabled = false,
  approveLabel = "\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34",
  onReject,
  rejectDisabled = false,
  rejectLabel = "\u0e2a\u0e48\u0e07\u0e01\u0e25\u0e31\u0e1a",
  onDisapprove,
  disapproveDisabled = false,
  disapproveLabel = "\u0e44\u0e21\u0e48\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34",
  onCancel,
  cancelDisabled = false,
  cancelLabel = "\u0e22\u0e01\u0e40\u0e25\u0e34\u0e01\u0e40\u0e2d\u0e01\u0e2a\u0e32\u0e23",
  onTimeline,
  timelineDisabled = false,
  isTimelineLoading = false,
  timelineLabel = "\u0e2a\u0e16\u0e32\u0e19\u0e30\u0e01\u0e32\u0e23\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34",
  onExtraAction,
  extraActionDisabled = false,
  extraActionLabel = THAI_EXTRA_ACTION_LABEL,
  extraActionClassName = "min-w-[180px] border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800",
  extraActions = [],
}: BottomActionBarProps) {
  const {
    handleSave,
    isSaving,
    saveError,
    saveMessage,
    handleSubmitWorkflow,
    isSubmittingWorkflow,
    submitError,
    submitMessage,
  } = usePermitRequestActionsContext()

  const resolvedError = error ?? submitError ?? saveError ?? null
  const resolvedMessage = message ?? submitMessage ?? saveMessage ?? null

  return (
    <div className="sticky bottom-0 left-0 right-0 border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-white/75">
      <div className="flex w-full items-center gap-4 px-4 py-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {onTimeline ? (
            <Button
              type="button"
              variant="outline"
              className="min-w-[150px]"
              onClick={onTimeline}
              disabled={disabled || timelineDisabled || isSaving || isTimelineLoading}
            >
              {isTimelineLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {"\u0e01\u0e33\u0e25\u0e31\u0e07\u0e42\u0e2b\u0e25\u0e14..."}
                </>
              ) : (
                timelineLabel
              )}
            </Button>
          ) : null}
          <span>
            {resolvedError ? (
              <span className="text-red-600">{resolvedError}</span>
            ) : resolvedMessage ? (
              <span className="text-green-600">{resolvedMessage}</span>
            ) : (
              <span className="text-gray-500">
                {"\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01\u0e40\u0e1e\u0e37\u0e48\u0e2d\u0e40\u0e01\u0e47\u0e1a\u0e40\u0e1b\u0e47\u0e19\u0e09\u0e1a\u0e31\u0e1a\u0e23\u0e48\u0e32\u0e07"}
              </span>
            )}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {onDisapprove ? (
            <Button
              type="button"
              variant="outline"
              className="min-w-[140px] border-red-500 text-red-600 hover:bg-red-50"
              onClick={onDisapprove}
              disabled={disabled || disapproveDisabled || isSaving}
            >
              {disapproveLabel}
            </Button>
          ) : null}
          {onReject ? (
            <Button
              type="button"
              variant="outline"
              className="min-w-[120px] border-red-500 text-red-600 hover:bg-red-50"
              onClick={onReject}
              disabled={disabled || rejectDisabled || isSaving}
            >
              {rejectLabel}
            </Button>
          ) : null}
          {onApprove ? (
            <Button
              type="button"
              className="min-w-[140px] bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={onApprove}
              disabled={disabled || approveDisabled || isSaving}
            >
              {approveLabel}
            </Button>
          ) : null}
          {onCancel ? (
            <Button
              type="button"
              className="min-w-[140px] bg-red-600 text-white hover:bg-red-700"
              onClick={onCancel}
              disabled={disabled || cancelDisabled || isSaving || isSubmittingWorkflow}
            >
              {cancelLabel}
            </Button>
          ) : null}
          {onSimulate ? (
            <Button
              type="button"
              variant="outline"
              onClick={onSimulate}
              disabled={disabled || isSaving || isSimulating}
            >
              {isSimulating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {"\u0e01\u0e33\u0e25\u0e31\u0e07\u0e08\u0e33\u0e25\u0e2d\u0e07..."}
                </>
              ) : (
                "\u0e08\u0e33\u0e25\u0e2d\u0e07 Workflow"
              )}
            </Button>
          ) : null}
          {onReference ? (
            <Button
              type="button"
              variant="outline"
              className="min-w-[140px] border-sky-500 text-sky-700 hover:bg-sky-50"
              onClick={onReference}
              disabled={referenceDisabled || isSaving || isSubmittingWorkflow}
            >
              {referenceLabel}
            </Button>
          ) : null}
          {extraActions.map((action) => (
            <Button
              key={action.label}
              type="button"
              variant="outline"
              className={action.className ?? extraActionClassName}
              onClick={action.onClick}
              disabled={disabled || action.disabled || isSaving || isSubmittingWorkflow}
            >
              {action.label}
            </Button>
          ))}
          {onExtraAction ? (
            <Button
              type="button"
              variant="outline"
              className={extraActionClassName}
              onClick={onExtraAction}
              disabled={disabled || extraActionDisabled || isSaving || isSubmittingWorkflow}
            >
              {extraActionLabel}
            </Button>
          ) : null}
          {showSubmitButton ? (
            <Button
              type="button"
              className="min-w-[140px] bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={onSubmit ?? handleSubmitWorkflow}
              disabled={disabled || submitDisabled || isSubmittingWorkflow || isSaving}
            >
              {isSubmittingWorkflow ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {"\u0e01\u0e33\u0e25\u0e31\u0e07\u0e2a\u0e48\u0e07..."}
                </>
              ) : (
                submitLabel
              )}
            </Button>
          ) : null}
          {showSaveButton ? (
            <Button
              type="button"
              variant="outline"
              className="min-w-[120px]"
              onClick={handleSave}
              disabled={disabled || saveDisabled || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {"\u0e01\u0e33\u0e25\u0e31\u0e07\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01..."}
                </>
              ) : (
                "\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01\u0e23\u0e48\u0e32\u0e07"
              )}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
